/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { useRef, useState, useMemo, useEffect, forwardRef } from "react";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import SlickGrid, { typedColumns, type GridRowBase } from ".";
import ModalStateDialogComponent from "../modal/ModalWithState";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import { useTranslation } from "@i18n";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import Text from "../Text/Text";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { Formatters, type SortDirection } from "slickgrid-react";
import { InputAdornment } from "@mui/material";
import IconComponent from "@CubeComponents/icons";

const SPECIAL_COLUMN_IDS = new Set(["_checkbox_selector", "_move"]);

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

export interface GridColumn extends GridRowBase {
  id: string;
  name: string;
  field?: string;
  hidden?: boolean;
  index?: number;
  hasChild?: boolean;
  parentId?: string | null;
  treeLevel?: number;
  isGroupColumn?: boolean;
}

const GridColumnChooser = forwardRef(({ onSave, onCancel }: any, ref) => {
  const { t } = useTranslation();
  const dataGrid = ref as any;
  const gridRef = useRef<any>(null);

  const selectedIdsRef = useRef<string[]>([]);
  const columnsRef = useRef<GridColumn[]>([]);

  const specialColumns = useRef<any>([]);

  const [searchValue, setSearchValue] = useState("");
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [gridRendered, setGridRendered] = useState<any>({});

  const [initialDataset, setInitialDataset] = useState<GridColumn[]>([]);

  useEffect(() => {
    if (!dataGrid.current) return;

    const gridService = dataGrid.current.gridService;
    const slickGrid = dataGrid.current.slickGrid;
    const allColumns = gridService?.getAllColumnDefinitions() || [];

    const visibleIds = slickGrid.getColumns().map((col: GridColumn) => col.id);
    const visibleSet = new Set(visibleIds);
    const visibleIndexMap = new Map(visibleIds.map((id: any, i: number) => [id, i]));

    const getSortIndex = (id: string) =>
      (visibleIndexMap.has(id) ? visibleIndexMap.get(id)! : Number.MAX_SAFE_INTEGER) as any;

    const specialCols: GridColumn[] = [];
    const groupedCols: Record<string, { groupName: string; minIndex: number; cols: any[] }> = {};
    const orphanCols: { minIndex: number; col: any }[] = [];

    for (const col of allColumns) {
      if (SPECIAL_COLUMN_IDS.has(col.id) || col.excludeFromColumnPicker) {
        specialCols.push(col);
        continue;
      }

      const sortIndex = getSortIndex(col.id) as any;

      const groupKey = col.columnGroupId || col.columnGroup;
      if (groupKey) {
        if (!groupedCols[groupKey]) {
          groupedCols[groupKey] = { groupName: col.columnGroup, minIndex: Number.MAX_SAFE_INTEGER, cols: [] };
        }
        const group = groupedCols[groupKey];
        group.cols.push(col);
        if (sortIndex < group.minIndex) group.minIndex = sortIndex;
      } else {
        orphanCols.push({ minIndex: sortIndex, col });
      }
    }

    specialColumns.current = specialCols;

    const sortedGroups = Object.entries(groupedCols).map(([groupKey, group]) => ({
      type: "group" as const,
      id: groupKey,
      groupName: group.groupName,
      minIndex: group.minIndex,
      cols: group.cols.sort((a, b) => getSortIndex(a.id) - getSortIndex(b.id)),
    }));

    const sortedOrphans = orphanCols.map((o) => ({
      type: "orphan" as const,
      id: o.col.id,
      minIndex: o.minIndex,
      col: o.col,
    }));

    const mergedList = [...sortedGroups, ...sortedOrphans].sort((a, b) => a.minIndex - b.minIndex);

    let finalFlatList: any[] = [];

    for (const item of mergedList) {
      if (item.type === "orphan") {
        finalFlatList.push({
          ...item.col,
          parentId: null,
          hasChild: false,
          __hasChildren: false,
          treeLevel: 0,
        });
      } else {
        finalFlatList.push({
          id: item.id,
          name: item.groupName,
          field: item.id,
          isGroupColumn: true,
          parentId: null,
          hasChild: true,
          __collapsed: false,
          __hasChildren: true,
          hidden: false,
          treeLevel: 0,
        });

        item.cols.forEach((col: any) => {
          finalFlatList.push({
            ...col,
            parentId: item.id,
            hasChild: false,
            __hasChildren: false,
            treeLevel: 1,
          });
        });
      }
    }

    finalFlatList = finalFlatList.map((col: any, index: number) => ({ ...col, index }));
    columnsRef.current = finalFlatList;
    selectedIdsRef.current = visibleIds;

    const dataset = finalFlatList.map((col: any) => ({
      ...col,
      hidden: col.isGroupColumn ? false : !visibleSet.has(col.id),
    }));

    setInitialDataset(dataset);
  }, [gridRendered, dataGrid]);

  useEffect(() => {
    if (initialDataset.length > 0 && gridRef.current?.dataView) {
      gridRef.current.dataView.setItems(initialDataset);
      gridRef.current.slickGrid?.invalidate?.();
      gridRef.current.slickGrid?.render?.();
    }
  }, [initialDataset]);

  const updateGridData = (searchVal: string = searchValue, showSelected: boolean = showOnlySelected) => {
    const dv = gridRef.current?.dataView;
    if (!dv) return;

    const allCols = columnsRef.current;
    const selectedSet = new Set(selectedIdsRef.current);

    let displayIds = new Set<string>();

    if (showSelected) {
      selectedSet.forEach((id: any) => displayIds.add(id));

      selectedSet.forEach((id: any) => {
        const col = allCols.find((c: any) => c.id === id);
        if (col?.parentId) displayIds.add(col.parentId);
      });
    } else {
      allCols.forEach((c: any) => displayIds.add(c.id));
    }

    if (searchVal) {
      const regex = new RegExp(escapeRegExp(searchVal), "i");

      const baseVisible = allCols.filter((c: any) => displayIds.has(c.id));

      const matched = baseVisible.filter((c: any) => regex.test(c.name));

      const searchIds = new Set<string>();

      matched.forEach((col: any) => {
        searchIds.add(col.id);
        if (col.parentId) searchIds.add(col.parentId);
      });

      displayIds = searchIds;
    }

    const finalData = allCols
      .filter((c: any) => displayIds.has(c.id))
      .map((c: any) => ({
        ...c,
        hidden: !selectedSet.has(c.id),

        parentId: c.parentId ?? null,
        hasChild: c.hasChild ?? false,
        treeLevel: c.treeLevel,
        isGroupColumn: c.isGroupColumn,
      }));

    dv.setItems(finalData);
    gridRef.current?.slickGrid?.invalidate();
    gridRef.current?.slickGrid?.render();
  };

  const onBeforeMoveRows = (e: Event, data: any): boolean => {
    const dv = gridRef.current?.dataView;
    if (!dv) return false;

    const { rows, insertBefore } = data;

    for (const r of rows) {
      const item = dv.getItem(r);

      if (item?.parentId) {
        e.stopPropagation();
        return false;
      }

      const targetItem = dv.getItem(insertBefore);
      if (targetItem?.parentId) {
        e.stopPropagation();
        return false;
      }

      if (r === insertBefore || r === insertBefore - 1) {
        e.stopPropagation();
        return false;
      }
    }

    return true;
  };

  const onMoveRows = (_e: Event, args: any) => {
    const { rows, insertBefore } = args;
    const dv = gridRef.current?.dataView;
    const slick = gridRef.current?.slickGrid;
    if (!dv) return;

    const flat = [...dv.items];

    const blocks: { start: number; items: any[] }[] = [];

    rows
      .sort((a: any, b: any) => a - b)
      .forEach((parentRowIndex: any) => {
        const parent = flat[parentRowIndex];

        if (parent.parentId) return;

        const blockItems = [parent];
        let pointer = parentRowIndex + 1;

        while (pointer < flat.length && flat[pointer].parentId === parent.id) {
          blockItems.push(flat[pointer]);
          pointer++;
        }

        blocks.push({
          start: parentRowIndex,
          items: blockItems,
        });
      });

    let newList = [...flat];

    blocks
      .slice()
      .reverse()
      .forEach((block: any) => {
        newList.splice(block.start, block.items.length);
      });

    let adjustedInsertBefore = insertBefore;
    blocks.forEach((block: any) => {
      if (block.start < insertBefore) {
        adjustedInsertBefore -= block.items.length;
      }
    });

    const combinedBlockItems = blocks.flatMap((b) => b.items);

    newList.splice(adjustedInsertBefore, 0, ...combinedBlockItems);

    const finalDataset = newList.map((row: any, index: number) => ({
      ...row,
      index,
      parentId: row.parentId ?? null,
      hasChild: row.hasChild ?? false,
      treeLevel: row.treeLevel,
      isGroupColumn: row.isGroupColumn,
      hidden: !selectedIdsRef.current.includes(row.id),
    }));

    columnsRef.current = finalDataset;

    dv.setItems(finalDataset);
    slick?.resetActiveCell?.();
    slick?.invalidate?.();
    slick?.render?.();
  };

  const handleCheckChange = (rowData: GridColumn, evtOrChecked: any) => {
    const nextChecked = typeof evtOrChecked === "boolean" ? evtOrChecked : !!evtOrChecked?.target?.checked;

    let updated = new Set(selectedIdsRef.current);

    const isParent = rowData.hasChild === true;
    const isChild = rowData.parentId !== null;

    const allCols = columnsRef.current;

    if (isParent) {
      const children = allCols.filter((col: any) => col.parentId === rowData.id);

      if (nextChecked) {
        updated.add(rowData.id);
        children.forEach((child: any) => updated.add(child.id));
      } else {
        updated.delete(rowData.id);
        children.forEach((child: any) => updated.delete(child.id));
      }
    } else if (isChild) {
      if (nextChecked) updated.add(rowData.id);
      else updated.delete(rowData.id);
    } else {
      if (nextChecked) updated.add(rowData.id);
      else updated.delete(rowData.id);
    }

    const order = allCols.map((r: any) => r.id);
    const finalSelected = Array.from(updated).sort((a, b) => order.indexOf(a) - order.indexOf(b));

    selectedIdsRef.current = finalSelected;

    const dv = gridRef.current?.dataView;
    if (!dv) return;

    // CUBE-17624: when "Show only selected" is on, an unchecked row must
    // disappear from the list (it's no longer selected). Re-run the search +
    // showOnlySelected filter against the freshly-updated `selectedIdsRef`
    // so the predicate reads current state, not the prior dataView snapshot.
    if (showOnlySelected) {
      updateGridData(searchValue, showOnlySelected);
      return;
    }

    const items = (dv.items || []).map((r: GridColumn) => ({
      ...r,
      hidden: !finalSelected.includes(r.id),

      parentId: r.parentId ?? null,
      hasChild: r.hasChild ?? false,
      treeLevel: r.treeLevel,
      isGroupColumn: r.isGroupColumn,
    }));

    dv.setItems(items);
    gridRef.current?.slickGrid?.invalidate();
    gridRef.current?.slickGrid?.render();
  };

  const checkBoxComponentTemplate = (_row: number, _cell: number, _value: any, _col: any, data: GridColumn) => {
    const container = document.createElement("div");
    const root = createRoot(container);

    let indeterminate = false;
    let checked = false;

    if (data.isGroupColumn) {
      const childCols = columnsRef.current.filter((col: any) => col.parentId === data.id);

      const selectedSet = new Set(selectedIdsRef.current);

      const allSelected = childCols.length > 0 && childCols.every((col: any) => selectedSet.has(col.id));

      const someSelected = !allSelected && childCols.some((col: any) => selectedSet.has(col.id));

      if (allSelected) checked = true;
      else if (someSelected) indeterminate = true;
    } else {
      checked = selectedIdsRef.current.includes(data.id);
    }

    root.render(
      <CheckBoxComponent
        indeterminate={indeterminate}
        checked={checked}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckChange(data, e)}
      />,
    );

    return container;
  };

  const columnDefinitions = useMemo(
    () =>
      typedColumns<GridColumn>([
        {
          id: "checkboxColumnChooser",
          field: "id",
          name: "",
          maxWidth: 50,
          formatter: checkBoxComponentTemplate as unknown as undefined,
        },
        {
          id: "label",
          name: t("Field"),
          field: "name",
          sortable: true,
          minWidth: 300,
          formatter: Formatters.tree as unknown as undefined,
        },
        { id: "index", name: t("Index"), field: "index", sortable: true, hidden: true },
      ]),

    [],
  );

  const gridOptions = useMemo(
    () => ({
      enableCheckboxSelector: false,
      enableRowMoveManager: !showOnlySelected,
      enableFiltering: true,
      forceFitColumns: true,

      rowMoveManager: { onBeforeMoveRows, onMoveRows },
      enableTreeData: true,
      treeDataOptions: {
        columnId: "index",
        parentPropName: "parentId",
        levelPropName: "treeLevel",
        indentMarginLeft: 25,
        initiallyCollapsed: false,
        hasChildrenPropName: "hasChild",
        initialSort: {
          columnId: "index",
          direction: "ASC" as SortDirection,
        },
      },
    }),
    [showOnlySelected],
  );

  const handleSave = () => {
    const orderedColumns = columnsRef.current.filter((col: any) => selectedIdsRef.current.includes(col.id));
    onSave?.(orderedColumns.filter((col: any) => !col.isGroupColumn && !col.hidden).map((col: any) => col.id));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    updateGridData(newValue, showOnlySelected);
  };

  const toggleShowOnlySelected = () => {
    const newValue = !showOnlySelected;
    setShowOnlySelected(newValue);
    updateGridData(searchValue, newValue);
  };

  /**
   * Auto-scroll the grid viewport while the user is dragging a row near the
   * top/bottom edge. Slickgrid's built-in RowMoveManager doesn't support
   * auto-scroll; without this, rows drop silently when the target is off-screen.
   *
   * CUBE-15135: SlickGrid's RowMoveManager only recomputes `insertBefore` on
   * native mousemove (it reads `pageY` then). Auto-scrolling the viewport
   * without firing a mousemove leaves the drop target stuck on whatever row
   * the user was hovering BEFORE the scroll, so the drop lands on the wrong
   * row (or is silently rejected). Re-dispatch the last MouseEvent to the
   * canvas after every scroll tick so the drag guide tracks the new viewport
   * content.
   */
  useEffect(() => {
    const dataGrid = ref as any;
    const container = dataGrid?.current?.slickGrid?.getContainerNode?.();
    if (!container) return;

    const EDGE_PX = 40;
    const SCROLL_PX = 12;
    let rafId: number | null = null;
    let lastClientY = 0;
    let lastClientX = 0;

    const getViewport = (): HTMLElement | null =>
      container.querySelector(".slick-viewport-left") ||
      container.querySelector(".slick-viewport") ||
      container.querySelector(".slick-viewport-right");

    const dispatchSyntheticMove = (): void => {
      const canvas = container.querySelector(".grid-canvas") as HTMLElement | null;
      const target = canvas ?? container;
      target.dispatchEvent(
        new MouseEvent("mousemove", {
          bubbles: true,
          cancelable: true,
          clientX: lastClientX,
          clientY: lastClientY,
          view: window,
        }),
      );
    };

    const tick = () => {
      rafId = null;
      const isDragging = document.querySelector(".slick-reorder-proxy") != null;
      if (!isDragging) return;
      const viewport = getViewport();
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      let delta = 0;
      if (lastClientY < rect.top + EDGE_PX) delta = -SCROLL_PX;
      else if (lastClientY > rect.bottom - EDGE_PX) delta = SCROLL_PX;
      if (delta !== 0) {
        const before = viewport.scrollTop;
        viewport.scrollTop += delta;
        if (viewport.scrollTop !== before) dispatchSyntheticMove();
        rafId = requestAnimationFrame(tick);
      }
    };

    const updatePointer = (clientX: number, clientY: number): void => {
      const isDragging = document.querySelector(".slick-reorder-proxy") != null;
      if (!isDragging) return;
      lastClientX = clientX;
      lastClientY = clientY;
      if (rafId == null) rafId = requestAnimationFrame(tick);
    };

    const onMouseMove = (e: MouseEvent) => updatePointer(e.clientX, e.clientY);
    const onDragOver = (e: DragEvent) => updatePointer(e.clientX, e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) updatePointer(t.clientX, t.clientY);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("touchmove", onTouchMove);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [gridRendered, ref]);

  return (
    <ModalStateDialogComponent
      footerContent={
        <div className="flex gap-4 pt-1">
          <ButtonComponent onClick={handleSave} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
            <Text>{t("Save")}</Text>
          </ButtonComponent>
          <ButtonComponent onClick={onCancel || (() => {})} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      footerBorderTop
      footerBorderColor="var(--stroke-primary)"
      headerColor="#E8F1FC"
      headerTitle={<Text className="font-bold">{t("Choose Columns")}</Text>}
      onCancel={onCancel || (() => {})}
      isVisible
      modelContent={
        <div className="h-full flex flex-col">
          <div className="flex flex-row justify-between mb-4 items-center">
            <div className="w-80">
              <TextFieldComponent
                type="text"
                value={searchValue}
                onChange={handleSearch}
                placeholder={t("Search Column")}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconComponent name="SearchIcon" width="1rem" height="1rem" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>
            <div className="flex flex-row gap-2 items-center">
              <ToggleSwitchComponent isChecked={showOnlySelected} onChange={toggleShowOnlySelected} />
              <Text className="text-sm">{t("Show only Selected")}</Text>
            </div>
          </div>
          <div className="flex-1">
            <SlickGrid
              ref={gridRef}
              surpassedDataset={initialDataset}
              hideNoDataIllustration
              disableHeader
              onReactGridCreated={(reactGrid: any) => {
                setGridRendered(reactGrid);
              }}
              hasChildMapping="hasChild"
              parentIdMapping="parentId"
              dropdownColumn={2}
              treeGrid
              columnDefinitions={columnDefinitions}
              gridOptions={gridOptions}
              showPageCount={false}
              disableCheckBoxSelection
              enableSelectAllCheckbox={false}
              disableChildDataFetch={true}
            />
          </div>
        </div>
      }
      width="80rem"
      height="45rem"
    />
  );
});

GridColumnChooser.displayName = "GridColumnChooser";

export default GridColumnChooser;
